import { createElement } from "react";

import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import { Box, Chip, Snackbar, Typography } from "@mui/material";
import { colors } from "@rubi/ui";
import ReactDOMServer from "react-dom/server";
import { ElementFactory, Question, Serializer, SvgRegistry } from "survey-core";
import { localization } from "survey-creator-core";
import {
  ReactQuestionFactory,
  SurveyQuestionElementBase,
} from "survey-react-ui";

import { tQuestion } from "@/components/online-reporting/custom-questions/i18nQuestion";

import {
  applyParsedLicenseToSurvey,
  applyPlaceToSurvey,
  DEFAULT_FIELD_PREFIX,
} from "./applyToSurvey";
import { getDecoder, type DecoderEngine } from "./decoder";
import { ScannerModal } from "./ScannerModal";
import type { IPlace, ParsedLicense, StoredScanBlob } from "./types";

export const DRIVERS_LICENSE_SCANNER_TYPE = "drivers-license-scanner";

export class QuestionDriversLicenseScannerModel extends Question {
  getType() {
    return DRIVERS_LICENSE_SCANNER_TYPE;
  }

  get fieldPrefix(): string {
    const val = this.getPropertyValue("fieldPrefix");
    return typeof val === "string" && val.length > 0
      ? val
      : DEFAULT_FIELD_PREFIX;
  }
  set fieldPrefix(val: string) {
    this.setPropertyValue("fieldPrefix", val);
  }

  get decoderEngine(): DecoderEngine {
    return this.getPropertyValue("decoderEngine") === "wasm"
      ? "wasm"
      : "browser";
  }
  set decoderEngine(val: DecoderEngine) {
    this.setPropertyValue("decoderEngine", val);
  }
}

export function registerDriversLicenseScanner(): void {
  if (
    !ElementFactory.Instance.getAllTypes().includes(
      DRIVERS_LICENSE_SCANNER_TYPE,
    )
  ) {
    ElementFactory.Instance.registerElement(
      DRIVERS_LICENSE_SCANNER_TYPE,
      (name) => new QuestionDriversLicenseScannerModel(name),
    );
  }

  const locale = localization.getLocale("");
  // i18n: builder-palette/property label localized in PR 3 (portal builder pass)
  locale.qt[DRIVERS_LICENSE_SCANNER_TYPE] = "Driver's License Scanner";

  const svg = ReactDOMServer.renderToString(
    <DocumentScannerIcon style={{ fill: colors.text.disabled }} />,
  );
  SvgRegistry.registerIconFromSvg(DRIVERS_LICENSE_SCANNER_TYPE, svg);

  if (!Serializer.findClass(DRIVERS_LICENSE_SCANNER_TYPE)) {
    Serializer.addClass(
      DRIVERS_LICENSE_SCANNER_TYPE,
      [
        {
          name: "fieldPrefix",
          default: DEFAULT_FIELD_PREFIX,
          category: "general",
          // i18n: builder-palette/property label localized in PR 3 (portal builder pass)
          displayName: "Field prefix",
        },
      ],
      () => new QuestionDriversLicenseScannerModel(""),
      "question",
    );
  }

  if (!Serializer.findProperty(DRIVERS_LICENSE_SCANNER_TYPE, "decoderEngine")) {
    Serializer.addProperty(DRIVERS_LICENSE_SCANNER_TYPE, {
      name: "decoderEngine",
      // i18n: builder property label localized in PR 3 (portal builder pass)
      displayName: "Decoder engine",
      category: "general",
      choices: ["browser", "wasm"],
      default: "browser",
    });
  }

  ReactQuestionFactory.Instance.registerQuestion(
    DRIVERS_LICENSE_SCANNER_TYPE,
    (props) => createElement(SurveyQuestionDriversLicenseScanner, props),
  );
}

interface ShellState {
  modalOpen: boolean;
  toastOpen: boolean;
  toastMessage: string;
}

export class SurveyQuestionDriversLicenseScanner extends SurveyQuestionElementBase {
  declare state: ShellState;

  constructor(props: any) {
    super(props);
    this.state = { modalOpen: false, toastOpen: false, toastMessage: "" };
  }

  get question(): QuestionDriversLicenseScannerModel {
    return this.questionBase as QuestionDriversLicenseScannerModel;
  }

  get blob(): StoredScanBlob | undefined {
    const v = this.question.value;
    if (v && typeof v === "object" && "scannedAt" in v) {
      return v as StoredScanBlob;
    }
    return undefined;
  }

  openModal = () => this.setState({ modalOpen: true });
  closeModal = () => this.setState({ modalOpen: false });

  handleScanResult = (
    parsed: ParsedLicense,
    placePromise: Promise<IPlace | null>,
  ) => {
    const survey = this.question.survey;
    // Fill the scalar fields and close immediately. The address is handled in
    // the background below so confirming never waits on the (best-effort)
    // reverse-geocode.
    const apply = survey
      ? applyParsedLicenseToSurvey(
          parsed,
          null,
          survey as any,
          this.question.fieldPrefix,
        )
      : { filledFields: [] };
    const blob: StoredScanBlob = {
      scannedAt: new Date().toISOString(),
      filledFields: apply.filledFields,
      geocoded: false,
    };
    this.question.value = blob;
    this.setState({
      modalOpen: false,
      toastOpen: true,
      toastMessage: buildToastMessage(apply.filledFields.length, parsed),
    });

    // Background: when the reverse-geocode resolves, fill the address field.
    // A failed or partial-match geocode resolves to null and leaves the
    // address for the user to complete via the autocomplete field.
    void placePromise
      .then((place) => {
        if (!place || !survey) return;
        // Bail if the user re-scanned while this geocode was in flight — the
        // stored blob now belongs to a newer scan and must not be clobbered.
        const current = this.blob;
        if (!current || current.scannedAt !== blob.scannedAt) return;
        const placeApply = applyPlaceToSurvey(
          place,
          survey as any,
          this.question.fieldPrefix,
        );
        if (placeApply.filledFields.length === 0) return;
        this.question.value = {
          ...current,
          filledFields: [...current.filledFields, ...placeApply.filledFields],
          geocoded: true,
        };
      })
      .catch(() => {
        // Geocode enrichment is best-effort; ignore failures.
      });
  };

  closeToast = () => this.setState({ toastOpen: false });

  renderDesignMode(): JSX.Element {
    const survey = this.question.survey as any;
    const prefix = this.question.fieldPrefix;
    const hasTargets = !!survey
      ?.getAllQuestions()
      .some(
        (q: any) => typeof q.name === "string" && q.name.startsWith(prefix),
      );
    return (
      <Box
        sx={{
          p: 2,
          border: "1px dashed",
          borderColor: "divider",
          borderRadius: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Typography variant="body2">
          {tQuestion("driversLicenseScanner.designModeInfo", { prefix })}
        </Typography>
        {!hasTargets && (
          <Chip
            size="small"
            color="warning"
            label={tQuestion("driversLicenseScanner.noTargetsWarning", {
              prefix,
            })}
            data-testid="scanner-no-targets-warning"
          />
        )}
      </Box>
    );
  }

  renderElement(): JSX.Element {
    if (this.question.isDesignMode) return this.renderDesignMode();

    const blob = this.blob;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {/* Native <button> (not MUI) so it inherits SurveyJS's `sd-btn`
            styling and sits visually with the surrounding survey chrome.
            Inline style — not `sx` — because `sx` only applies to MUI elements. */}
        <button
          type="button"
          className="sd-btn sd-btn--small"
          onClick={this.openModal}
          disabled={this.props.isDisplayMode || this.question.isReadOnly}
          style={{
            alignItems: "center",
            display: "inline-flex",
            gap: 6,
            width: "fit-content",
          }}
        >
          <DocumentScannerIcon fontSize="small" />
          {blob
            ? tQuestion("driversLicenseScanner.rescanButton")
            : tQuestion("driversLicenseScanner.scanButton")}
        </button>
        {blob && (
          <Typography variant="caption" sx={{ color: "success.main" }}>
            {tQuestion("driversLicenseScanner.scannedOn")}
          </Typography>
        )}
        {this.state.modalOpen && (
          <ScannerModal
            open={true}
            onResult={this.handleScanResult}
            onCancel={this.closeModal}
            decoder={getDecoder(this.question.decoderEngine)}
          />
        )}
        <Snackbar
          open={this.state.toastOpen}
          autoHideDuration={6000}
          onClose={this.closeToast}
          message={this.state.toastMessage}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        />
      </Box>
    );
  }
}

function buildToastMessage(filledCount: number, parsed: ParsedLicense): string {
  if (filledCount === 0) {
    const hasAnyParsedField = !!(
      parsed.firstName ||
      parsed.lastName ||
      parsed.dob ||
      parsed.sex ||
      parsed.driverLicenseNo ||
      parsed.driverLicenseJurisdiction ||
      parsed.address
    );
    return hasAnyParsedField
      ? tQuestion("driversLicenseScanner.toastNoMatchingFields")
      : tQuestion("driversLicenseScanner.toastNoUsefulFields");
  }
  return tQuestion("driversLicenseScanner.toastFilled", { count: filledCount });
}
